import { useState, useRef, useEffect } from "react";
import { CalendarDaysIcon } from "@heroicons/react/24/outline";
import {
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameDay,
  isSameMonth,
  isBefore,
  isAfter,
  format,
} from "date-fns";
import { de } from "date-fns/locale";

const stripTime = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

export default function DatePicker({
  value,
  onChange,
  min,
  max,
  className = "",
  // Ersetzt das Aussehen des Auslösers vollständig. Der Standard stammt aus
  // den Buchungsstrecken; im Kopf der Startseite sitzt derselbe Kalender in
  // einer anderen Umgebung, und zwei konkurrierende Tailwind-Klassen für
  // dieselbe Eigenschaft ("rounded" und "rounded-xl") gewinnt mal die eine,
  // mal die andere.
  triggerClassName = "",
  platzhalter = "Datum wählen",
  // Der Kalender bringt sein Symbol selbst mit — am Ende des Knopfes. In
  // einer Suchleiste, in der alle Felder ihr Symbol vorn tragen, wäre das
  // das einzige, das hinten steht.
  zeigeIcon = true,
  id,
  remainingByDate,
  requestedCount = 1,
  onMonthChange,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(startOfMonth(value || new Date()));
  const containerRef = useRef(null);

  // Absprache 2026-08-18: Live-Verfügbarkeit — Parent lädt bei jedem
  // Monatswechsel (und beim Öffnen) neu nach, remainingByDate kommt als Map
  // "yyyy-MM-dd" -> Anzahl noch freier Kameras rein (fehlt ein Tag, gilt er
  // als verfügbar). "Nicht genug frei" wird erst hier mit requestedCount
  // verglichen, damit ein Tag nicht nur gesperrt, sondern mit der
  // tatsächlichen Restmenge angezeigt werden kann (Absprache 2026-08-17:
  // sonst wirkt "ausgebucht" wie eine Sackgasse, obwohl eine kleinere
  // Anzahl an demselben Tag noch ginge).
  useEffect(() => {
    if (isOpen) onMonthChange?.(viewMonth);
  }, [isOpen, viewMonth]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setViewMonth(startOfMonth(value || new Date()));
    }
  }, [isOpen]);

  const gridStart = startOfWeek(startOfMonth(viewMonth), { weekStartsOn: 1 });
  const gridEnd = endOfWeek(endOfMonth(viewMonth), { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

  // Tage vor der Mindestvorlaufzeit (z.B. Lieferzeit) sind so oder so nicht
  // buchbar, unabhängig von der Verfügbarkeit — da wäre eine Restmengen-
  // Anzeige nur verwirrend ("noch 6 verfügbar", obwohl der Tag längst aus
  // einem ganz anderen Grund gesperrt ist). Absprache 2026-08-17.
  const isLeadTimeBlocked = (day) => {
    if (min && isBefore(day, stripTime(min))) return true;
    if (max && isAfter(day, stripTime(max))) return true;
    return false;
  };

  const remainingFor = (day) => {
    if (!remainingByDate || isLeadTimeBlocked(day)) return undefined;
    const key = format(day, "yyyy-MM-dd");
    return remainingByDate[key];
  };

  const isInsufficient = (day) => {
    const remaining = remainingFor(day);
    return remaining !== undefined && remaining < requestedCount;
  };

  const isDisabled = (day) => {
    if (isLeadTimeBlocked(day)) return true;
    if (isInsufficient(day)) return true;
    return false;
  };

  const prevMonthDisabled = min && !isAfter(viewMonth, startOfMonth(min));
  const nextMonthDisabled = max && !isBefore(viewMonth, startOfMonth(max));

  const handleSelect = (day) => {
    if (isDisabled(day)) return;
    onChange(day);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        id={id}
        onClick={() => setIsOpen((open) => !open)}
        className={
          triggerClassName ||
          `shadow appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline flex items-center justify-between text-left bg-white ${className}`
        }
      >
        <span>
          {value
            ? format(value, "EEE, dd.MM.yyyy", { locale: de })
            : platzhalter}
        </span>
        {zeigeIcon && (
          <CalendarDaysIcon className="w-5 h-5 flex-none text-slate-400" />
        )}
      </button>

      {isOpen && (
        <div className="absolute z-20 mt-1 bg-white border rounded shadow-lg p-3 w-72">
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              disabled={prevMonthDisabled}
              onClick={() => setViewMonth((m) => subMonths(m, 1))}
              className="p-1 hover:bg-slate-100 rounded text-lg leading-none disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed"
              aria-label="Vorheriger Monat"
            >
              ‹
            </button>
            <span className="font-bold capitalize">
              {format(viewMonth, "MMMM yyyy", { locale: de })}
            </span>
            <button
              type="button"
              disabled={nextMonthDisabled}
              onClick={() => setViewMonth((m) => addMonths(m, 1))}
              className="p-1 hover:bg-slate-100 rounded text-lg leading-none disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed"
              aria-label="Nächster Monat"
            >
              ›
            </button>
          </div>
          <div className="grid grid-cols-7 text-center text-xs text-slate-500 mb-1">
            {WEEKDAYS.map((d) => (
              <span key={d}>{d}</span>
            ))}
          </div>
          <div className="grid grid-cols-7 text-center gap-y-1">
            {days.map((day) => {
              const disabled = isDisabled(day);
              const remaining = remainingFor(day);
              const insufficient = isInsufficient(day);
              const soldOut = remaining === 0;
              const limited = insufficient && !soldOut;
              const selected = value && isSameDay(day, value);
              const outside = !isSameMonth(day, viewMonth);
              const title = soldOut
                ? "Ausgebucht"
                : limited
                  ? `Nur noch ${remaining} verfügbar`
                  : undefined;
              return (
                <button
                  type="button"
                  key={day.toISOString()}
                  disabled={disabled}
                  onClick={() => handleSelect(day)}
                  title={title}
                  className={[
                    "w-9 h-9 rounded text-sm relative",
                    selected
                      ? "bg-[#5FCBE4] text-white hover:bg-[#5FCBE4]"
                      : soldOut
                        ? "bg-red-100 text-red-400 cursor-not-allowed"
                        : limited
                          ? "bg-orange-100 text-orange-700 cursor-not-allowed"
                          : disabled
                            ? "text-slate-300 cursor-not-allowed"
                            : "hover:bg-[#5FCBE4]/20 cursor-pointer",
                    outside && !disabled ? "text-slate-400" : "",
                  ].join(" ")}
                >
                  {format(day, "d")}
                  {limited && (
                    <span className="absolute bottom-0 inset-x-0 text-[8px] leading-none font-semibold">
                      noch {remaining}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {(() => {
            // Absprache 2026-08-17/19: Legende nur mit den Einträgen, die
            // auch tatsächlich vorkommen (nicht pauschal beide immer
            // anzeigen) — aber über ALLE sichtbaren Tage im Grid, inklusive
            // Vor-/Nachlauf-Tage aus dem Nachbarmonat (die werden ja auch
            // eingefärbt angezeigt, sonst fehlt für sie die Erklärung).
            const hasSoldOut = days.some((day) => remainingFor(day) === 0);
            const hasLimited = days.some((day) => isInsufficient(day) && remainingFor(day) !== 0);
            if (!hasSoldOut && !hasLimited) return null;
            return (
              <div className="flex flex-col gap-1 mt-3 pt-2 border-t text-xs text-slate-600">
                {hasLimited && (
                  <div className="flex items-center gap-2">
                    <span className="w-3.5 h-3.5 rounded bg-orange-100 shrink-0" />
                    <span>Nur noch wenige Kameras verfügbar</span>
                  </div>
                )}
                {hasSoldOut && (
                  <div className="flex items-center gap-2">
                    <span className="w-3.5 h-3.5 rounded bg-red-100 shrink-0" />
                    <span>Ausgebucht</span>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
